import { Link } from "react-router";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { blogPosts } from "../data/blogPosts";
import { Calendar, User, Tag } from "lucide-react";

export default function Blog() {
  const getCategoryColor = (category: string) => {
    switch (category) {
      case "partnership":
        return { bg: "rgba(30,142,175,0.1)", text: "#1e8eaf" };
      case "announcement":
        return { bg: "rgba(255,204,16,0.1)", text: "#ffcc10" };
      case "research":
        return { bg: "rgba(24,75,157,0.1)", text: "#184b9d" };
      case "update":
        return { bg: "rgba(144,202,243,0.1)", text: "#90CAF3" };
      default:
        return { bg: "rgba(5,14,26,0.1)", text: "#050e1d" };
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="bg-white min-h-screen">
      <SEO
        title="News & Updates - Unpatterned"
        description="Latest announcements, partnerships, and updates from Unpatterned. Stay informed about our programs, research, and community initiatives."
        keywords={[
          "unpatterned news",
          "announcements",
          "partnerships",
          "updates",
          "blog",
          "YES Program updates",
          "technology education news",
          "Africa tech partnerships",
        ]}
        url="https://unpatterned.org/blog"
      />
      <Header />

      {/* Hero Section */}
      <section className="pt-40 pb-20 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="font-['DM_Serif_Display',serif] text-6xl text-[#050e1d] mb-6">
            News & Updates
          </h1>
          <p className="font-['Manrope',sans-serif] text-xl text-[#050e1d]/70 max-w-3xl mx-auto">
            Announcements, partnerships, and insights from the Unpatterned community
          </p>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="pb-24 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="space-y-12">
            {blogPosts.map((post) => {
              const categoryStyle = getCategoryColor(post.category);
              
              return (
                <article
                  key={post.id}
                  className="bg-white border border-[rgba(5,14,26,0.1)] rounded-2xl p-8 hover:shadow-lg transition-all"
                >
                  <div className="flex flex-wrap gap-4 items-center mb-4">
                    <span
                      className="px-3 py-1 rounded-full text-sm font-['Manrope',sans-serif] font-medium capitalize flex items-center gap-2"
                      style={{ backgroundColor: categoryStyle.bg, color: categoryStyle.text }}
                    >
                      <Tag size={14} />
                      {post.category}
                    </span>
                    {post.featured && (
                      <span className="px-3 py-1 rounded-full text-sm font-['Manrope',sans-serif] font-medium bg-[rgba(255,204,16,0.15)] text-[#050e1d]">
                        Featured
                      </span>
                    )}
                  </div>

                  <Link to={`/blog/${post.id}`}>
                    <h2 className="font-['DM_Serif_Display',serif] text-3xl text-[#050e1d] mb-3 hover:text-[#184b9d] transition-colors">
                      {post.title}
                    </h2>
                  </Link>

                  <p className="font-['Manrope',sans-serif] text-lg text-[#050e1d]/60 mb-4 italic">
                    {post.subtitle}
                  </p>

                  <p className="font-['Manrope',sans-serif] text-[#050e1d]/70 mb-6 leading-relaxed">
                    {post.excerpt}
                  </p>

                  <div className="flex items-center gap-6 text-sm font-['Manrope',sans-serif] text-[#050e1d]/50 mb-6">
                    <div className="flex items-center gap-2">
                      <Calendar size={16} />
                      {formatDate(post.date)}
                    </div>
                    <div className="flex items-center gap-2">
                      <User size={16} />
                      {post.author.name}
                    </div>
                  </div>

                  <Link
                    to={`/blog/${post.id}`}
                    className="inline-flex items-center gap-2 text-[#184b9d] font-['Manrope',sans-serif] font-medium hover:gap-3 transition-all"
                  >
                    Read full article
                    <span>→</span>
                  </Link>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
